import React, { useState } from 'react';
import { DataTable } from '@/components/shared';
import { List } from '@refinedev/antd';
import { endPoints } from '@/api/configs';
import { useList } from '@/hooks/useList';
import { useTranslation } from '@refinedev/core';
import {
  GlobalOutlined,
  MessageFilled,
  PlusOutlined,
  CodeOutlined,
} from '@ant-design/icons';
import { getColumnsDomains } from './columnsDomains';
import { Button, message, Space } from 'antd';
import {
  AddDomainModal,
  AddContactUsEmailModal,
  AddEmbedPlatformModal,
  EditEmbedPlatformModal,
} from '@/components/modals';
import useAPI from '@/api/useApi';
import { getColumnsAdminEmail } from './columnsAdminEmail';
import { GetConfigs } from '@/api/types/getSystemConfigs';
import { useProfileStore } from '@/core';
import { getColumnsEmbedPlatforms } from './columnsEmbedPlatforms';

export const SystemConfigList = () => {
  const { userData } = useProfileStore();
  const adminType = userData?.account?.admin?.admin_type;
  const { usePatch } = useAPI();
  const [domainPage, setDomainPage] = useState(1);
  const [emailPage, setEmailPage] = useState(1);
  const [embedPlatformPage, setEmbedPlatformPage] = useState(1);
  const [domainToDelete, setDomainToDelete] = useState<string>('null');
  const [emailToDelete, setEmailToDelete] = useState<string>('null');
  const [embedPlatformToDelete, setEmbedPlatformToDelete] =
    useState<string>('null');
  const [embedPlatformToEdit, setEmbedPlatformToEdit] = useState<string>('');
  const [isAddDomainModalOpen, setIsAddDomainModalOpen] = useState(false);
  const [isAddEmailModalOpen, setIsAddEmailModalOpen] = useState(false);
  const [isAddEmbedPlatformModalOpen, setIsAddEmbedPlatformModalOpen] =
    useState(false);
  const [isEditEmbedPlatformModalOpen, setIsEditEmbedPlatformModalOpen] =
    useState(false);
  const { translate } = useTranslation();
  const { data, isLoading, isRefetching, refetch } = useList<GetConfigs>({
    endpoint: endPoints.systemConfig,
  });
  const { mutate: deleteSystemConfig } = usePatch(endPoints.systemConfig);

  const handleDelete = async (
    field: string,
    value: string,
    successMessage: string
  ) => {
    await deleteSystemConfig(
      {
        operation: 'REMOVE',
        field,
        value,
      },
      {
        onSuccess: () => {
          message.success(translate(successMessage));
          refetch();
        },
        onError: (error) => {
          message.error(error.message);
        },
      }
    );
  };

  const handleDeleteDomain = () =>
    handleDelete(
      'WHITELISTED_DOMAIN',
      domainToDelete,
      'system-config.domains.deleted-success'
    );

  const handleDeleteEmail = () =>
    handleDelete(
      'ADMIN_EMAIL',
      emailToDelete,
      'system-config.contact-us.deleted'
    );

  const handleDeleteEmbedPlatform = () =>
    handleDelete(
      'ALLOWED_EMBED_PLATFORM',
      embedPlatformToDelete,
      'system-config.embed-platforms.deleted-success'
    );

  const getTableData = (dataKey: keyof GetConfigs) => {
    const items = data?.data.data[dataKey] || [];
    const listItems = Array.isArray(items) ? items : [];
    return {
      data: listItems,
      headers: {
        // @ts-ignore
        'x-total-count': listItems.length,
      },
    };
  };

  const columns = getColumnsDomains(
    translate,
    handleDeleteDomain,
    setDomainToDelete,
    adminType
  );
  const columnsAdminEmail = getColumnsAdminEmail(
    translate,
    handleDeleteEmail,
    setEmailToDelete,
    adminType
  );
  const columnsEmbedPlatforms = getColumnsEmbedPlatforms(
    translate,
    handleDeleteEmbedPlatform,
    setEmbedPlatformToDelete,
    setEmbedPlatformToEdit,
    setIsEditEmbedPlatformModalOpen,
    adminType
  );

  const handleAddDomain = () => {
    setIsAddDomainModalOpen(true);
  };
  const handleAddEmail = () => {
    setIsAddEmailModalOpen(true);
  };
  const handleAddEmbedPlatform = () => {
    setIsAddEmbedPlatformModalOpen(true);
  };

  const onPageChange = (
    page: number,
    type: 'domain' | 'email' | 'embedPlatform'
  ) => {
    if (type === 'domain') {
      setDomainPage(page);
    } else if (type === 'email') {
      setEmailPage(page);
    } else {
      setEmbedPlatformPage(page);
    }
  };

  return (
    <div>
      <List
        title={
          <div className="flex gap-x-3">
            <MessageFilled />
            <h1>{translate('system-config.contact-us.contact-us-email')}</h1>
          </div>
        }
        canCreate={false}
        headerButtonProps={{
          hidden: adminType !== 'SUPER_ADMIN',
        }}
        headerButtons={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddEmail}
              loading={isLoading}
            >
              {translate('buttons.create')}
            </Button>
          </Space>
        }
      >
        <DataTable
          pageSize={5}
          columns={columnsAdminEmail}
          loading={isLoading || isRefetching}
          currentPage={emailPage}
          onPageChange={(page) => onPageChange(page, 'email')}
          // @ts-ignore
          data={getTableData('admins_email')}
        />
        <AddContactUsEmailModal
          open={isAddEmailModalOpen}
          onCancel={() => setIsAddEmailModalOpen(false)}
          refetch={refetch}
        />
      </List>
      <List
        title={
          <div className="flex gap-x-3">
            <GlobalOutlined />
            <h1>{translate('system-config.domains.title')}</h1>
          </div>
        }
        canCreate={false}
        headerButtonProps={{
          hidden: adminType !== 'SUPER_ADMIN',
        }}
        headerButtons={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddDomain}
              loading={isLoading}
            >
              {translate('buttons.create')}
            </Button>
          </Space>
        }
      >
        <DataTable
          pageSize={5}
          columns={columns}
          loading={isLoading || isRefetching}
          currentPage={domainPage}
          onPageChange={(page) => onPageChange(page, 'domain')}
          // @ts-ignore
          data={getTableData('whitelisted_domains')}
        />
        <AddDomainModal
          open={isAddDomainModalOpen}
          onCancel={() => setIsAddDomainModalOpen(false)}
          refetch={refetch}
        />
      </List>

      <List
        title={
          <div className="flex gap-x-3">
            <CodeOutlined />
            <h1>{translate('system-config.embed-platforms.title')}</h1>
          </div>
        }
        canCreate={false}
        headerButtonProps={{
          hidden: adminType !== 'SUPER_ADMIN',
        }}
        headerButtons={
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddEmbedPlatform}
              loading={isLoading}
            >
              {translate('buttons.add')}
            </Button>
          </Space>
        }
      >
        <DataTable
          pageSize={5}
          columns={columnsEmbedPlatforms}
          loading={isLoading || isRefetching}
          currentPage={embedPlatformPage}
          onPageChange={(page) => onPageChange(page, 'embedPlatform')}
          // @ts-ignore
          data={getTableData('allowed_embed_platforms')}
        />
        <AddEmbedPlatformModal
          open={isAddEmbedPlatformModalOpen}
          onCancel={() => setIsAddEmbedPlatformModalOpen(false)}
          refetch={refetch}
        />
        <EditEmbedPlatformModal
          open={isEditEmbedPlatformModalOpen}
          onCancel={() => setIsEditEmbedPlatformModalOpen(false)}
          refetch={refetch}
          currentName={embedPlatformToEdit}
        />
      </List>
    </div>
  );
};
